import React, { useEffect, useMemo, useState, FormEvent } from 'react';
import {
  Button,
  ButtonGroup,
  Alert,
  Row,
  Col,
  FormGroup,
  FormControl,
  ControlLabel,
} from 'react-bootstrap';
import { MessageType } from '@upstart/harmony/lib/legos/websiteTypes';
import DealershipApiService, {
  LendingDealerData,
  LendingDealerUpdateData,
  LendingDealerAddress,
} from '../../../services/DealershipApiService';
import { PageMessage } from '../../GlobalUI';
import '../DealerEditor.scss';

interface Props {
  dealerId: string;
  onBack?: () => void;
  fetching: boolean;
  hideButtons?: boolean;
}

const normalizeData = (data: LendingDealerData): LendingDealerData => ({
  name: data.name || '',
  timezone: data.timezone || '',
  phone: data.phone || '',
  reserveEmail: data.reserveEmail || '',
  address: {
    lineOne: data.address?.lineOne || '',
    lineTwo: data.address?.lineTwo || '',
    city: data.address?.city || '',
    state: data.address?.state || '',
    zip: data.address?.zip || '',
  },
  bankAccount: data.bankAccount,
  enabled: data.enabled,
  updatedAt: data.updatedAt,
  slug: data.slug,
});

const ARLLendingConfig = ({
  dealerId,
  onBack,
  fetching,
  hideButtons = false,
}: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<LendingDealerData>({
    name: '',
    timezone: '',
    phone: '',
    reserveEmail: '',
    address: {
      lineOne: '',
      lineTwo: '',
      city: '',
      state: '',
      zip: '',
    },
  });
  const [originalData, setOriginalData] = useState<LendingDealerData | null>(
    null,
  );

  useEffect(() => {
    const fetchData = async () => {
      if (!dealerId) return;

      setIsLoading(true);
      setError(null);

      try {
        const data = await DealershipApiService.getLendingDealer(dealerId);
        const normalizedData = normalizeData(data);
        setFormData(normalizedData);
        setOriginalData(normalizedData);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to fetch ARL lending data',
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [dealerId]);

  const handleInputChange =
    (field: 'name' | 'timezone' | 'phone' | 'reserveEmail') =>
    (e: FormEvent<FormControl>) => {
      const value = (e.target as HTMLInputElement).value;
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleAddressChange =
    (field: keyof LendingDealerAddress) => (e: FormEvent<FormControl>) => {
      const value = (e.target as HTMLInputElement).value;
      setFormData((prev) => ({
        ...prev,
        address: {
          ...prev.address,
          [field]: value,
        },
      }));
    };

  const hasChanges = useMemo(() => {
    if (!originalData) return false;
    return (
      formData.name !== originalData.name ||
      formData.timezone !== originalData.timezone ||
      formData.phone !== originalData.phone ||
      formData.reserveEmail !== originalData.reserveEmail ||
      formData.address.lineOne !== originalData.address.lineOne ||
      formData.address.lineTwo !== originalData.address.lineTwo ||
      formData.address.city !== originalData.address.city ||
      formData.address.state !== originalData.address.state ||
      formData.address.zip !== originalData.address.zip
    );
  }, [formData, originalData]);

  const handleSave = async () => {
    if (!dealerId || !hasChanges) return;

    setIsSaving(true);
    try {
      const updateData: LendingDealerUpdateData = {
        name: formData.name,
        timezone: formData.timezone,
        phone: formData.phone,
        reserveEmail: formData.reserveEmail,
        address: formData.address,
      };

      await DealershipApiService.updateLendingDealer(dealerId, updateData);

      setOriginalData({ ...formData });
    } catch (err) {
      PageMessage({
        type: MessageType.ERROR,
        message:
          err instanceof Error
            ? err.message
            : 'Failed to save ARL lending configuration',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const isDisabled = isLoading || isSaving || fetching;

  return (
    <div className="arl-lending-config">
      <fieldset>
        <legend>ARL Lending Configuration</legend>

        {error && <Alert bsStyle="danger">{error}</Alert>}

        {isLoading ? (
          <div>Loading ARL lending data...</div>
        ) : (
          <>
            <Row>
              <Col sm={6}>
                <FormGroup>
                  <ControlLabel>Seller-Creditor Legal Name</ControlLabel>
                  <FormControl
                    type="text"
                    id="arl-name"
                    value={formData.name}
                    onChange={handleInputChange('name')}
                    disabled={isDisabled}
                  />
                </FormGroup>
              </Col>
              <Col sm={6}>
                <FormGroup>
                  <ControlLabel>Timezone</ControlLabel>
                  <FormControl
                    type="text"
                    id="arl-timezone"
                    value={formData.timezone}
                    onChange={handleInputChange('timezone')}
                    disabled={isDisabled}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col sm={6}>
                <FormGroup>
                  <ControlLabel>Phone</ControlLabel>
                  <FormControl
                    type="text"
                    id="arl-phone"
                    value={formData.phone}
                    onChange={handleInputChange('phone')}
                    disabled={isDisabled}
                    data-dd-privacy="mask"
                  />
                </FormGroup>
              </Col>
              <Col sm={6}>
                <FormGroup>
                  <ControlLabel>Reserve Email</ControlLabel>
                  <FormControl
                    type="email"
                    id="arl-reserveEmail"
                    value={formData.reserveEmail}
                    onChange={handleInputChange('reserveEmail')}
                    disabled={isDisabled}
                    data-dd-privacy="mask"
                  />
                </FormGroup>
              </Col>
            </Row>

            <h5>Address</h5>
            <Row>
              <Col sm={8}>
                <FormGroup>
                  <ControlLabel>Address Line 1</ControlLabel>
                  <FormControl
                    type="text"
                    id="arl-lineOne"
                    value={formData.address.lineOne}
                    onChange={handleAddressChange('lineOne')}
                    disabled={isDisabled}
                  />
                </FormGroup>
              </Col>
              <Col sm={4}>
                <FormGroup>
                  <ControlLabel>Address Line 2</ControlLabel>
                  <FormControl
                    type="text"
                    id="arl-lineTwo"
                    value={formData.address.lineTwo}
                    onChange={handleAddressChange('lineTwo')}
                    disabled={isDisabled}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col sm={4}>
                <FormGroup>
                  <ControlLabel>City</ControlLabel>
                  <FormControl
                    type="text"
                    id="arl-city"
                    value={formData.address.city}
                    onChange={handleAddressChange('city')}
                    disabled={isDisabled}
                  />
                </FormGroup>
              </Col>
              <Col sm={4}>
                <FormGroup>
                  <ControlLabel>State</ControlLabel>
                  <FormControl
                    type="text"
                    id="arl-state"
                    value={formData.address.state}
                    onChange={handleAddressChange('state')}
                    disabled={isDisabled}
                  />
                </FormGroup>
              </Col>
              <Col sm={4}>
                <FormGroup>
                  <ControlLabel>Zip</ControlLabel>
                  <FormControl
                    type="text"
                    id="arl-zip"
                    value={formData.address.zip}
                    onChange={handleAddressChange('zip')}
                    disabled={isDisabled}
                  />
                </FormGroup>
              </Col>
            </Row>

            {formData.bankAccount && (
              <>
                <h5>Bank Account (Read Only)</h5>
                <Row>
                  <Col sm={4}>
                    <FormGroup>
                      <ControlLabel>Account Holder Name</ControlLabel>
                      <FormControl
                        type="text"
                        value={formData.bankAccount.accountHolderName || ''}
                        disabled
                        data-dd-privacy="mask"
                      />
                    </FormGroup>
                  </Col>
                  <Col sm={4}>
                    <FormGroup>
                      <ControlLabel>Account Number</ControlLabel>
                      <FormControl
                        type="text"
                        value={formData.bankAccount.accountNumber || ''}
                        disabled
                        data-dd-privacy="mask"
                      />
                    </FormGroup>
                  </Col>
                  <Col sm={4}>
                    <FormGroup>
                      <ControlLabel>Routing Number</ControlLabel>
                      <FormControl
                        type="text"
                        value={formData.bankAccount.routingNumber || ''}
                        disabled
                        data-dd-privacy="mask"
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </>
            )}

            {formData.updatedAt && (
              <p className="text-muted arl-lending-updated-at">
                Last Updated: {new Date(formData.updatedAt).toLocaleString()}
              </p>
            )}
          </>
        )}
      </fieldset>

      {!hideButtons && (
        <ButtonGroup className="arl-lending-button-group">
          <Button disabled={isDisabled} type="button" onClick={onBack}>
            {'<< Back'}
          </Button>
          <Button
            bsClass="btn btn-primary btn-large"
            disabled={isDisabled || !hasChanges}
            type="button"
            onClick={handleSave}
          >
            {isSaving ? 'Saving...' : 'Save ARL Lending'}
          </Button>
        </ButtonGroup>
      )}
      {hideButtons && hasChanges && (
        <Button
          bsStyle="primary"
          disabled={isDisabled}
          type="button"
          onClick={handleSave}
          className="arl-lending-save-btn"
        >
          {isSaving ? 'Saving...' : 'Save ARL Lending Changes'}
        </Button>
      )}
    </div>
  );
};

export default ARLLendingConfig;
